import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { and, not } from 'ember-truth-helpers';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import HotelBenchmarkInsightTopBanner from 'lumen/components/hotel/benchmark-insight/top-banner';
import type { BeniMeta, BeniMetaComparisonSet } from 'lumen/models';
import {
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  BENI_COMPETITORS_TABS,
  COMPARISON_SET_DATA_POINTS_MISSING_REASONS,
  COMPARISON_SET_MISSING_REASONS,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';

interface HotelBenchmarkInsightTopBannersSignature {
  Args: {
    meta: BeniMeta;
    primaryComparisonSetType: ComparisonSet;
    secondaryComparisonSetType: ComparisonSet;
  };
}

export default class HotelBenchmarkInsightTopBanners extends Component<HotelBenchmarkInsightTopBannersSignature> {
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked hasDismissedCompsetValidityBanner = false;
  @tracked hasDismissedHiddenDataBanner = false;
  @tracked hasDismissedNotEnoughHotelsBanner = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetValidityBannerContent() {
    const { primaryComparisonSetMeta, secondaryComparisonSetMeta } = this;

    const compsetComparisonSetWithMissingReasonCompsetValidity = [
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    ]
      .filter((comparisonSet) => comparisonSet !== null)
      .find((comparisonSet) => {
        return (
          comparisonSet.missingReason ===
          COMPARISON_SET_MISSING_REASONS.COMPSET_VALIDITY
        );
      });

    if (!compsetComparisonSetWithMissingReasonCompsetValidity) {
      return null;
    }

    if (
      compsetComparisonSetWithMissingReasonCompsetValidity.comparisonSetType ===
      BENI_COMPARISON_SETS.COMPSET
    ) {
      return {
        title: this.intl.t(
          'benchmark_insight.banners.compset_validity.compset.title',
        ),
        message: this.intl.t(
          'benchmark_insight.banners.compset_validity.compset.message',
        ),
        hasCta: true,
        primaryAction: {
          label: this.intl.t(
            'benchmark_insight.banners.compset_validity.compset.cta',
          ),
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          action: () =>
            this.router.transitionTo(
              'hotel.settings.competitors.benchmark.compset',
            ),
        },
      };
    }

    if (
      compsetComparisonSetWithMissingReasonCompsetValidity.comparisonSetType ===
      BENI_COMPARISON_SETS.INTERNAL_COMPSET
    ) {
      return {
        title: this.intl.t(
          'benchmark_insight.banners.compset_validity.internal_compset.title',
        ),
        message: this.intl.t(
          'benchmark_insight.banners.compset_validity.internal_compset.message',
        ),
      };
    }

    return {
      title: this.intl.t(
        'benchmark_insight.banners.compset_validity.compset.title',
      ),
      message: this.intl.t(
        'benchmark_insight.competitors.smart_compset.warning.banner',
      ),
      hasCta: true,
      primaryAction: {
        label: this.intl.t(
          'benchmark_insight.banners.compset_validity.compset.cta',
        ),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        action: () =>
          this.router.transitionTo('hotel.settings.competitors.benchmark', {
            queryParams: {
              activeTab: BENI_COMPETITORS_TABS.SMART_COMPSET,
            },
          }),
      },
    };
  }

  get displayDefaultBanner(): boolean {
    const otherReasons = [
      this.hiddenDataBannerContent,
      this.compsetValidityBannerContent,
    ].filter(Boolean);

    return !otherReasons.length && Boolean(this.notEnoughHotelsBannerContent);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hiddenDataBannerContent() {
    const { primaryComparisonSetMeta, secondaryComparisonSetMeta } = this;

    // Check for comparison sets with missing_reason: stay_date_range in the meta.
    if (
      [primaryComparisonSetMeta, secondaryComparisonSetMeta]
        .filter((comparisonSet) => comparisonSet !== null)
        .find(
          (comparisonSet) =>
            comparisonSet.missingReason ===
            COMPARISON_SET_MISSING_REASONS.STAY_DATE_RANGE,
        )
    ) {
      return {
        title: this.intl.t('benchmark_insight.banners.missing_data.title'),
        message: this.intl.t('benchmark_insight.banners.missing_data.message'),
      };
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get notEnoughHotelsBannerContent() {
    const { primaryComparisonSetMeta, secondaryComparisonSetMeta } = this;

    const comparisonSetsWithMissingReasonNotEnoughHotels = [
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    ]
      // Separate filter to satisfy TS
      .filter((comparisonSet) => comparisonSet !== null)
      .filter(
        (comparisonSet) =>
          comparisonSet.isVisible &&
          comparisonSet.datapointsMissingReasons.includes(
            COMPARISON_SET_DATA_POINTS_MISSING_REASONS.NOT_ENOUGH_HOTELS,
          ),
      )
      .map((comparisonSet) => comparisonSet.comparisonSetType);

    if (!comparisonSetsWithMissingReasonNotEnoughHotels[0]) {
      return null;
    }

    const comparisonSetOne = this.intl.t(
      BENI_COMPARISON_SET_LONG_TRANSLATIONS[
        comparisonSetsWithMissingReasonNotEnoughHotels[0]
      ],
    );

    if (comparisonSetsWithMissingReasonNotEnoughHotels[1]) {
      const comparisonSetTwo = this.intl.t(
        BENI_COMPARISON_SET_LONG_TRANSLATIONS[
          comparisonSetsWithMissingReasonNotEnoughHotels[1]
        ],
      );
      return {
        message: this.intl.t(
          'benchmark_insight.missing_data_points_banner.not_enough_hotels.two_comparison_sets',
          {
            comparisonSetOne,
            comparisonSetTwo,
          },
        ),
      };
    }

    return {
      message: this.intl.t(
        'benchmark_insight.missing_data_points_banner.not_enough_hotels.one_comparison_set',
        {
          comparisonSet: comparisonSetOne,
        },
      ),
    };
  }

  get ownComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta } = this.args;

    if (!meta) {
      return null;
    }

    return meta.getMetaForComparisonSet(BENI_COMPARISON_SETS.OWN);
  }

  get primaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, primaryComparisonSetType } = this.args;

    if (!meta || !primaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(primaryComparisonSetType);
  }

  get secondaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, secondaryComparisonSetType } = this.args;

    if (!meta || !secondaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(secondaryComparisonSetType);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  comparisonSetMetaProperty(comparisonSetMeta: BeniMetaComparisonSet | null) {
    return comparisonSetMeta ? comparisonSetMeta.comparisonSetType : null;
  }

  isCompsetOrPortfolio(comparisonSet: ComparisonSet | null): boolean {
    return (
      comparisonSet === BENI_COMPARISON_SETS.COMPSET ||
      comparisonSet === BENI_COMPARISON_SETS.INTERNAL_COMPSET
    );
  }

  @action
  dismissCompsetValidityBanner(): void {
    this.hasDismissedCompsetValidityBanner = true;
  }

  @action
  dismissHiddenDataBanner(): void {
    this.hasDismissedHiddenDataBanner = true;
  }

  @action
  dismissNotEnoughHotelsBanner(): void {
    this.hasDismissedNotEnoughHotelsBanner = true;
  }

  <template>
    {{#if
      (and
        this.displayDefaultBanner (not this.hasDismissedNotEnoughHotelsBanner)
      )
    }}
      <HotelBenchmarkInsightTopBanner
        @bannerContent={{this.notEnoughHotelsBannerContent}}
        @bannerStyle="warning"
        @closeBanner={{this.dismissNotEnoughHotelsBanner}}
        @dataTestId="not-enough-hotels-banner"
      />
    {{else}}
      {{#if
        (and
          this.hiddenDataBannerContent (not this.hasDismissedHiddenDataBanner)
        )
      }}
        <HotelBenchmarkInsightTopBanner
          @bannerContent={{this.hiddenDataBannerContent}}
          @bannerStyle="info"
          @closeBanner={{this.dismissHiddenDataBanner}}
          @dataTestId="hidden-data-banner"
        />
      {{/if}}

      {{#if
        (and
          this.compsetValidityBannerContent
          (not this.hasDismissedCompsetValidityBanner)
        )
      }}
        <HotelBenchmarkInsightTopBanner
          @bannerContent={{this.compsetValidityBannerContent}}
          @bannerStyle="warning"
          @closeBanner={{this.dismissCompsetValidityBanner}}
          @dataTestId="compset-validity-banner"
          @hasCta={{this.compsetValidityBannerContent.hasCta}}
          @icon="warning"
          @primaryAction={{this.compsetValidityBannerContent.primaryAction}}
        />
      {{/if}}
    {{/if}}
  </template>
}
